import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Zap, Crown, ArrowRight, Users, Briefcase, CreditCard, Shield } from "lucide-react";
import { useSubscription } from "@/hooks/use-subscription";
import { useLocation } from "wouter";

interface UpgradePromptProps {
  feature?: string;
  limitType?: 'users' | 'licenses' | 'applications' | 'people';
  variant?: 'banner' | 'modal' | 'card';
  className?: string;
  onUpgrade?: () => void;
}

export function UpgradePrompt({ feature, limitType, variant = 'card', className, onUpgrade }: UpgradePromptProps) {
  const { planType, limits, usage, getUserUsagePercentage, getLicenseUsagePercentage, getApplicationUsagePercentage, getPeopleUsagePercentage } = useSubscription();
  const [, setLocation] = useLocation();

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      setLocation('/subscription');
    }
  };

  // Show for all plans when they reach limits, not just free
  const isAtLimit = limitType && usage && limits && (() => {
    switch (limitType) {
      case 'users': return usage.users >= limits.maxUsers;
      case 'licenses': return usage.licenses >= limits.maxLicenses;
      case 'applications': return usage.applications >= limits.maxApplications;
      case 'people': return (usage.people || 0) >= limits.maxPeople;
      default: return false;
    }
  })();

  // Don't show if enterprise plan (unlimited) or not at limit
  if (planType === 'enterprise' || !isAtLimit) {
    return null;
  }

  const getIcon = () => {
    switch (limitType) {
      case 'users': return <Users className="h-5 w-5" />;
      case 'licenses': return <CreditCard className="h-5 w-5" />;
      case 'applications': return <Briefcase className="h-5 w-5" />;
      case 'people': return <Shield className="h-5 w-5" />;
      default: return <Crown className="h-5 w-5" />;
    }
  };

  const getLimitText = () => {
    switch (limitType) {
      case 'users': return `${usage.users}/${limits.maxUsers} users`;
      case 'licenses': return `${usage.licenses}/${limits.maxLicenses} licenses`;
      case 'applications': return `${usage.applications}/${limits.maxApplications} applications`;
      case 'people': return `${usage.people || 0}/${limits.maxPeople} people`;
      default: return '';
    }
  };

  const getUsagePercentage = () => {
    switch (limitType) {
      case 'users': return getUserUsagePercentage();
      case 'licenses': return getLicenseUsagePercentage();
      case 'applications': return getApplicationUsagePercentage();
      case 'people': return getPeopleUsagePercentage();
      default: return 0;
    }
  };

  if (variant === 'banner') {
    return (
      <div className={`bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-lg mb-6 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Crown className="h-6 w-6" />
            <div>
              <h3 className="font-semibold">Upgrade to unlock {feature || 'more features'}</h3>
              <p className="text-sm opacity-90">
                {limitType ? `You've reached your ${limitType} limit (${getLimitText()})` : 'Get access to advanced features and higher limits'}
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={handleUpgrade} className="bg-white text-purple-600 hover:bg-gray-100">
            Upgrade Now
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className={`border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {getIcon()}
            <CardTitle className="text-lg">Upgrade Required</CardTitle>
          </div>
          <Badge variant="outline" className="border-purple-300 text-purple-700">
            {planType === 'trial' ? 'Trial Plan' : `${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan`}
          </Badge>
        </div>
        <CardDescription>
          {feature 
            ? `Upgrade to access ${feature} and unlock your full potential`
            : limitType 
              ? `You've reached your ${limitType} limit. Upgrade to add more.`
              : 'Unlock advanced features with a paid plan'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {limitType && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Current usage</span>
              <span className="font-medium">{getLimitText()}</span>
            </div>
            <Progress value={getUsagePercentage()} className="h-2" />
          </div>
        )}
        
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Upgrade benefits:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center">
              <Zap className="h-3 w-3 mr-2 text-yellow-500" />
              Higher limits for users, licenses & apps
            </li>
            <li className="flex items-center">
              <Zap className="h-3 w-3 mr-2 text-yellow-500" />
              Advanced analytics & reporting
            </li>
            <li className="flex items-center">
              <Zap className="h-3 w-3 mr-2 text-yellow-500" />
              Priority support & custom integrations
            </li>
          </ul>
        </div>

        <Button onClick={handleUpgrade} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
          <Crown className="mr-2 h-4 w-4" />
          Upgrade Plan
        </Button>
      </CardContent>
    </Card>
  );
}